import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { StatusIndicator } from '@/components/ui/StatusIndicator';
import { Skeleton } from '@/components/ui/Skeleton';
import { useServiceHealth } from '@/hooks/useAdminMonitoring';
import { useMemo } from 'react';
import { HeartPulse } from 'lucide-react';

type IndicatorStatus = 'healthy' | 'degraded' | 'down' | 'success' | 'warning' | 'error' | 'neutral';

const validStatuses: IndicatorStatus[] = ['healthy', 'degraded', 'down', 'success', 'warning', 'error', 'neutral'];

function toIndicatorStatus(status: string): IndicatorStatus {
  return validStatuses.includes(status as IndicatorStatus) ? (status as IndicatorStatus) : 'neutral';
}

export function PlatformHealthCard() {
  const { data, isLoading } = useServiceHealth();

  // Health API returns { status, timestamp, services: { database: {...}, redis: {...}, minio: {...} } }
  const services = useMemo(() => {
    const servicesObj = data?.services;
    if (!servicesObj || typeof servicesObj !== 'object') return [];
    return Object.entries(servicesObj).map(([name, details]) => ({
      name,
      status: details?.status ?? 'unknown',
      latency: details?.latency ?? 0,
    }));
  }, [data]);

  return (
    <Card>
      <CardHeader title="Santé de la plateforme" icon={<HeartPulse className="h-4 w-4" />} />
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {services.length > 0 ? services.map((service) => (
              <div key={service.name} className="flex items-center justify-between rounded-xl bg-neutral-100/60 px-3 py-2.5 transition-colors duration-200 hover:bg-neutral-100 dark:bg-neutral-800/40 dark:hover:bg-neutral-800/60">
                <StatusIndicator status={toIndicatorStatus(service.status)} label={service.name} />
                <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-mono text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">{service.latency}ms</span>
              </div>
            )) : (
              <>
                {['API', 'PostgreSQL', 'Redis', 'MinIO', 'Puppeteer'].map((name) => (
                  <div key={name} className="flex items-center justify-between rounded-xl bg-neutral-100/60 px-3 py-2.5 transition-colors duration-200 hover:bg-neutral-100 dark:bg-neutral-800/40 dark:hover:bg-neutral-800/60">
                    <StatusIndicator status="healthy" label={name} />
                    <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-mono text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">--</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
